import React, { useState, useEffect } from 'react';
import { BeakerIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GasPod, GasMeterReading, CreateGasMeterReadingData, GasReadingStatus, GasReadingSource } from '../../types/gas';
import { gasPodService, gasMeterReadingService } from '../../services/gasService';
import { getGasDay } from '../../utils/gasCalendar';
import { useLanguage } from '../../contexts/LanguageContext';
import BulkGasReadingUploader from './BulkGasReadingUploader';
import toast from 'react-hot-toast';

interface GasMeterReadingsSectionProps {
  customerId: string;
}

const GasMeterReadingsSection: React.FC<GasMeterReadingsSectionProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [gasPods, setGasPods] = useState<GasPod[]>([]);
  const [readings, setReadings] = useState<GasMeterReading[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingReadingId, setEditingReadingId] = useState<string | null>(null);
  const [selectedPodId, setSelectedPodId] = useState<string>('');
  const [formData, setFormData] = useState<Partial<CreateGasMeterReadingData>>({
    gasDay: getGasDay().label,
    readingDate: new Date().toISOString().split('T')[0],
    status: 'read',
    source: 'manual',
    meterStatus: undefined,
    consumptionM3: undefined,
    energyKwh: undefined,
    calorificValue: undefined,
    correctionFactor: undefined,
    note: ''
  });

  const loadData = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [podData, readingData] = await Promise.all([
        gasPodService.getGasPodsByCustomerId(customerId),
        gasMeterReadingService.getGasMeterReadingsByCustomerId(customerId)
      ]);
      setGasPods(podData);
      setReadings(readingData);
      if (podData.length > 0 && !selectedPodId) {
        setSelectedPodId(podData[0].id);
      }
    } catch (error) {
      console.error('Error loading gas meter readings:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.readings') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId]);

  const resetForm = () => {
    setEditingReadingId(null);
    setSelectedPodId('');
    setFormData({
      gasDay: getGasDay().label,
      readingDate: new Date().toISOString().split('T')[0],
      status: 'read',
      source: 'manual',
      meterStatus: undefined,
      consumptionM3: undefined,
      energyKwh: undefined,
      calorificValue: undefined,
      correctionFactor: undefined,
      note: ''
    });
  };

  const handleAdd = async () => {
    if (!selectedPodId) {
      toast.error(t('gas.reading.errors.podRequired'));
      return;
    }
    if (!formData.gasDay) {
      toast.error(t('gas.reading.errors.gasDayRequired'));
      return;
    }

    try {
      const payload = {
        gasPodId: selectedPodId,
        gasDay: formData.gasDay,
        readingDate: formData.readingDate?.trim() || formData.gasDay,
        meterStatus: formData.meterStatus ? Number(formData.meterStatus) : undefined,
        consumptionM3: formData.consumptionM3 ? Number(formData.consumptionM3) : undefined,
        energyKwh: formData.energyKwh ? Number(formData.energyKwh) : undefined,
        calorificValue: formData.calorificValue ? Number(formData.calorificValue) : undefined,
        correctionFactor: formData.correctionFactor ? Number(formData.correctionFactor) : undefined,
        status: formData.status as GasReadingStatus || 'read',
        source: formData.source as GasReadingSource || 'manual',
        note: formData.note?.trim() || undefined
      };
      if (editingReadingId) {
        await gasMeterReadingService.updateGasMeterReading(editingReadingId, payload);
        toast.success(t('gas.messages.saveSuccess', { name: t('gas.reading.gasDay') }));
      } else {
        await gasMeterReadingService.createGasMeterReading({ customerId, ...payload });
        toast.success(t('gas.messages.addSuccess', { name: t('gas.reading.gasDay') }));
      }
      setShowAdd(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating gas meter reading:', error);
      toast.error(t('gas.messages.addError', { name: t('gas.reading.gasDay') }));
    }
  };

  const handleEdit = (reading: GasMeterReading) => {
    setSelectedPodId(reading.gasPodId);
    setFormData({
      gasDay: reading.gasDay,
      readingDate: reading.readingDate || reading.gasDay,
      meterStatus: reading.meterStatus,
      consumptionM3: reading.consumptionM3,
      energyKwh: reading.energyKwh,
      calorificValue: reading.calorificValue,
      correctionFactor: reading.correctionFactor,
      status: reading.status,
      source: reading.source,
      note: reading.note || ''
    });
    setEditingReadingId(reading.id);
    setShowAdd(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('gas.messages.confirmDelete'))) return;
    try {
      await gasMeterReadingService.deleteGasMeterReading(id);
      toast.success(t('gas.messages.deleteSuccess', { name: t('gas.reading.gasDay') }));
      loadData();
    } catch (error) {
      console.error('Error deleting gas meter reading:', error);
      toast.error(t('gas.messages.deleteError', { name: t('gas.reading.gasDay') }));
    }
  };

  const getStatusLabel = (status: GasReadingStatus) => {
    switch (status) {
      case 'estimated': return 'Becsült';
      case 'read': return 'Leolvasott';
      case 'corrected': return 'Helyesbített';
      default: return status;
    }
  };

  const getSourceLabel = (source: GasReadingSource) => {
    switch (source) {
      case 'manual': return 'Kézi';
      case 'mscons': return 'MSCONS';
      case 'invoice': return 'Számla';
      case 'import': return 'Import';
      default: return source;
    }
  };

  const readingsByPod = gasPods.map(pod => ({
    pod,
    podReadings: readings.filter(r => r.gasPodId === pod.id)
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BeakerIcon className="h-6 w-6 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('gas.sections.readings')}</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          {showAdd ? t('gas.common.cancel') : t('gas.common.new')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('gas.common.loading')}</p>
      ) : gasPods.length === 0 ? (
        <p className="text-sm text-gray-500">{t('gas.pod.noPodAvailable')}</p>
      ) : (
        <div className="space-y-4">
          {readingsByPod.map(({ pod, podReadings }) => (
            <div key={pod.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-mono text-sm font-semibold text-gray-900">{pod.podId}</p>
                  <p className="text-xs text-gray-500">{pod.address.city}, {pod.address.street}</p>
                </div>
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">
                  {podReadings.length} állás
                </span>
              </div>
              {podReadings.length === 0 ? (
                <p className="text-xs text-gray-500 italic">{t('gas.common.noData')}</p>
              ) : (
                <div className="space-y-1">
                  {podReadings.slice(0, 5).map(reading => (
                    <div key={reading.id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900">{reading.gasDay}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{getStatusLabel(reading.status)}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full">{getSourceLabel(reading.source)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        {reading.meterStatus !== undefined && <span>{reading.meterStatus} m³</span>}
                        {reading.consumptionM3 !== undefined && <span>{reading.consumptionM3} m³ fogy.</span>}
                        {reading.energyKwh !== undefined && <span>{reading.energyKwh} kWh</span>}
                        <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(reading)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title={t('gas.common.edit')}
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(reading.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={t('gas.common.delete')}
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                      </div>
                    </div>
                  ))}
                  {podReadings.length > 5 && (
                    <p className="text-xs text-gray-500 italic">+ {podReadings.length - 5} további állás</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
          <h3 className="text-sm font-semibold text-green-900 mb-2">{t('gas.sections.readings')}</h3>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.gasPod')} *</label>
            <select
              value={selectedPodId}
              onChange={e => setSelectedPodId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">{t('gas.common.select')}</option>
              {gasPods.map(pod => (
                <option key={pod.id} value={pod.id}>{pod.podId} ({pod.address.city})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.gasDay')} *</label>
              <input
                type="date"
                value={formData.gasDay || ''}
                onChange={e => setFormData({ ...formData, gasDay: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.readingDate')}</label>
              <input
                type="date"
                value={formData.readingDate || ''}
                onChange={e => setFormData({ ...formData, readingDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.meterStatus')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.meterStatus || ''}
                onChange={e => setFormData({ ...formData, meterStatus: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.consumptionM3')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.consumptionM3 || ''}
                onChange={e => setFormData({ ...formData, consumptionM3: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.energyKwh')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.energyKwh || ''}
                onChange={e => setFormData({ ...formData, energyKwh: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.calorificValue')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.calorificValue || ''}
                onChange={e => setFormData({ ...formData, calorificValue: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.correctionFactor')}</label>
              <input
                type="number"
                step="0.0001"
                value={formData.correctionFactor || ''}
                onChange={e => setFormData({ ...formData, correctionFactor: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.status')}</label>
              <select
                value={formData.status || 'read'}
                onChange={e => setFormData({ ...formData, status: e.target.value as GasReadingStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="read">{t('gas.reading.statuses.read')}</option>
                <option value="estimated">{t('gas.reading.statuses.estimated')}</option>
                <option value="corrected">{t('gas.reading.statuses.corrected')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.reading.fields.note')}</label>
            <textarea
              value={formData.note || ''}
              onChange={e => setFormData({ ...formData, note: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              {t('gas.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
            >
              {t('gas.common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Tömeges mérőállás feltöltés */}
      <BulkGasReadingUploader
        customerId={customerId}
        gasPods={gasPods}
        onUploaded={loadData}
      />
    </div>
  );
};

export default GasMeterReadingsSection;
